import {
  POST_TYPES,
  NARRATIVE_TYPES_ARRAY,
  getNarrativeSlug,
  getNarrativeDescription,
  getLongNarrativeDescription,
} from "posts/constants";
import {
  datesFromRawData,
  getChartData,
  NARRATIVE_MEDIA_TYPES,
} from "narratives/constants";
import {
  BORDER_RADIUS,
  BORDER_RADIUS_SMALL,
  BORDER_RADIUS_XLARGE,
  BREAKPOINT,
  COLORS,
  DEFAULT_BOX_SHADOW,
  getNarrativeTypeColor,
} from "utils/styles";
import { format } from "date-fns";

import ArticlePost from "components/Posts/ArticlePost";
import MusicPost from "components/Posts/MusicPost";
import PhotoPost from "components/Posts/PhotoPost";
import TvPost from "components/Posts/TvPost";
import FlexSpacer from "components/FlexSpacer";
import TwitterPost from "components/Posts/TwitterPost";
import VideoPost from "components/Posts/VideoPost";
import NarrativeSVGGradients from "components/Narratives/NarrativeSVGGradients";
import NarrativeNav from "components/Narratives/NarrativeNav";
import ScrollControl from "components/Narratives/ScrollControl";
import Nav from "components/Nav/Nav";
import EditLink from "components/EditLink";

import ReactMarkdown from "react-markdown";
import MarkdownStrong from "components/Feed/MarkdownStrong";
import rehypeRaw from "rehype-raw";

import { MediumButton } from "components/Button";
import Pager from "components/Pager";

import Head from "next/head";
import { useRouter } from "next/router";
import Link from "next/link";

import React, { useRef, useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import uniq from "lodash.uniq";
import countBy from "lodash.countby";
import {
  VictoryArea,
  VictoryAxis,
  VictoryChart,
  VictoryLabel,
  VictoryScatter,
  VictoryTooltip,
  Point,
} from "victory";
import { parseISOLocal } from "utils/date";

import { NodeClient } from "@sentry/node";

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 40vw 1fr;
  background: #fff;
  width: 100vw;
  max-width: 100vw;

  @media ${BREAKPOINT.M} {
    display: block;
  }
`;

const InfoPanelWrapper = styled.div`
  background: #fff;
  height: 100%;
  width: 40vw;
  left: 0;
  top: 0;
  bottom: 0;
  position: relative;
  z-index: 10;
  max-height: 100vh;
  overflow-y: auto;

  @media ${BREAKPOINT.M} {
    height: 40vh;
    width: 100vw;
    top: auto;
    z-index: 100;
    position: fixed;
    bottom: 0;
    right: 0;
    border-top-left-radius: 25px;
    border-top-right-radius: 25px;
    box-shadow: 1.5px -2px 15px 0px rgba(0, 0, 0, 0.1);
  }
`;

const InfoPanelInnerWrapper = styled.div`
  width: 40vw;
  padding: 15vh 2rem 5rem 2rem;
  overflow-y: auto;
  position: fixed;
  flex-direction: column;
  justify-content: center;
  min-height: 100vh;
  height: 100%;

  @media ${BREAKPOINT.M} {
    overflow-y: visible;
    width: 100vw;
    padding: 2rem;
    position: static;
    padding-bottom: 5rem;
    height: initial;
    max-height: initial;
    min-height: initial;
  }
`;

const InfoPanelSection = styled.div`
  ${BORDER_RADIUS};
  padding: 1.25rem;

  p:last-child {
    margin-bottom: 0;
  }
`;

const NarrativeInfoPanel = styled(InfoPanelSection)`
  ${BORDER_RADIUS};
  background-color: ${({ backgroundColor, narrative }) =>
    backgroundColor ||
    (narrative ? getNarrativeTypeColor(narrative) : COLORS.LIGHTEST_GRAY)};
  max-height: ${({ expandable, narrative }) => {
    if (expandable) return narrative ? "30vh" : "initial";
  }};
  overflow-y: hidden;
  transition: max-height 300ms ease-out;
`;

const MainDisplay = styled.div`
  position: relative;
  height: 100vh;
  overflow-x: auto;
`;
const GraphWrapper = styled.div`
  background: #f9f9f9;
  height: 100vh;
  overflow-y: hidden;
  overflow-x: auto;
  position: relative;
  transition: width 300ms ease-out;

  svg {
    height: 100%;
  }

  @media ${BREAKPOINT.M} {
    padding-bottom: 30vh;
  }

  &::-webkit-scrollbar {
    display: none;
  }

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

const Title = styled.h2`
  display: flex;
  font-size: 1.125rem;
  line-height: 1.5rem;
  font-weight: 500;
  margin-bottom: 1rem;
  align-items: center;
`;

const TitleIcon = styled.i.attrs(() => ({
  className: "material-icons-round md-dark md-18 mr-1",
}))`
  line-height: 1.5rem;
`;

const TitleText = styled.span.attrs(() => ({
  className: "mabry-regular",
}))`
  display: block;
  flex-grow: 1;
`;

const NarrativeTitle = styled.h1.attrs({ className: "mabry-regular" })``;

const InfoPanelContent = styled.div`
  em {
    font-family: "mabry-light-italic" !important;
  }
`;

const NarrativeButton = styled(MediumButton).attrs()`
  display: inline-block;
  font-size: 1rem;
  cursor: pointer;
  background-color: ${({ backgroundColor }) =>
    backgroundColor || COLORS.GRAY} !important;
`;

const GraphAxis = styled.img`
  position: absolute;
  bottom: 4rem;
  left: 1rem;
  width: 9rem;
  height: auto;
  max-width: 20vw;
  pointer-events: none;
  touch-action: none;
`;

const TwitterNote = styled.div`
  position: absolute;
  bottom: 3.25rem;
  right: 1rem;
  left: 1rem;
  text-align: center;
  z-index: 100;
  font-size: 0.75rem;

  @media ${BREAKPOINT.M} {
    bottom: 1.25rem;
  }
`;

export default function Narratives({
  chartData,
  narrativeMilestones,
  narrative,
}) {
  const router = useRouter();

  useEffect(() => setActiveMilestone(-1), [narrative]);
  const graphWindowRef = useRef(null);

  const [activeMedia, setActiveMedia] = useState(null);

  const [highlightedNarrative, setHighlightedNarrative] = useState(null);

  const dataByNarrative = useMemo(() => {
    let data = {};
    NARRATIVE_TYPES_ARRAY.forEach((narrativeType) => {
      data[narrativeType] = getChartData(
        { narrativeTypes: narrativeType, media: activeMedia },
        chartData
      );
    });
    return data;
  }, [activeMedia]);

  const highlightNarrative = (newValue) => {
    if (narrative) return;
    setHighlightedNarrative(newValue);
  };

  useEffect(() => {
    if (narrative) setHighlightedNarrative(null);
  }, [narrative]);

  const domainHeight = useMemo(() => {
    return Math.ceil(
      Math.max(
        ...getChartData(
          {
            narrativeTypes: narrative ? narrative : NARRATIVE_TYPES_ARRAY,
            media: activeMedia,
          },
          chartData
        ).map(({ count }) => count)
      ) * 1.1
    );
  }, [activeMedia, narrative]);

  const [graphHeight, setGraphHeight] = useState(null);
  const [graphWidth, setGraphWidth] = useState(null);

  const [dataWindowSize, setDataWindowSize] = useState(365);
  const dataDates = datesFromRawData(chartData);
  const totalDataSize = dataDates.length;

  const dateLabelsWithSize = Object.entries(
    countBy(dataDates, (date) => new Date(date).getUTCFullYear())
  )
    .map(([year, size]) => ({
      year,
      size,
    }))
    .filter((a) => a.size > 1);

  const datapointWidth = dataWindowSize / 7;

  const width = ((totalDataSize * 7) / dataWindowSize) * graphWidth;
  const height = graphHeight;

  const areas = NARRATIVE_TYPES_ARRAY.map((narrativeType) => {
    const data = dataByNarrative[narrativeType];
    let active;
    if (narrative) active = narrative === narrativeType;
    else active = highlightedNarrative === narrativeType;

    const insightsData =
      narrative === narrativeType
        ? data.filter(({ hasInsights }) => hasInsights)
        : [];

    return (
      (!narrative || narrative === narrativeType) &&
      [
        <VictoryArea
          key={narrativeType}
          standalone={false}
          width={width}
          height={height}
          interpolation="catmullRom"
          padding={15}
          domainPadding={{ x: [0, 0], y: [0, 10] }}
          domain={{ y: [0, domainHeight] }}
          animate={{ duration: 300, onLoad: { duration: 0 } }}
          events={[
            {
              target: "data",
              eventHandlers: {
                onMouseOver: () => highlightNarrative(narrativeType),
                onMouseOut: () =>
                  highlightNarrative((prevHighlightedNarrative) =>
                    prevHighlightedNarrative === narrativeType
                      ? null
                      : prevHighlightedNarrative
                  ),
                onClick: () => {
                  router.push(`/narratives/${getNarrativeSlug(narrativeType)}`);
                },
              },
            },
          ]}
          style={{
            data: {
              stroke:
                narrative !== narrativeType &&
                highlightedNarrative === narrativeType
                  ? COLORS.BLACK
                  : "none",
              strokeDasharray: "1,3",
              fill: `url(#gradient-${getNarrativeSlug(narrativeType)}${
                narrative !== narrativeType &&
                highlightedNarrative === narrativeType
                  ? "-highlighted"
                  : ""
              })`,
            },
            parent: { border: "1px solid #ccc" },
          }}
          x="parsedDate"
          y="count"
          data={data}
        />,
        insightsData.length > 0 ? (
          <VictoryChart
            height={height}
            width={width}
            scale={{ x: "time" }}
            standalone={false}
            padding={15}
          >
            <VictoryScatter
              key={`${narrativeType}-scatter`}
              data={data}
              x="parsedDate"
              y={({ count, hasInsights }) => (hasInsights ? count : null)}
              standalone={false}
              width={width}
              height={height}
              interpolation="catmullRom"
              domainPadding={{ x: [0, 0], y: [0, 0] }}
              domain={{ y: [0, domainHeight] }}
              dataComponent={<Point />}
              events={[
                {
                  target: "data",
                  eventHandlers: {
                    onClick: (_, { datum }) =>
                      setActiveMilestoneByDate(datum.date),
                    onMouseEnter: () => [
                      {
                        mutation: () => ({
                          style: { strokeWidth: 3, stroke: COLORS.BLACK },
                        }),
                      },
                    ],
                    onMouseLeave: () => [
                      {
                        mutation: () => ({ style: { strokeWidth: 0 } }),
                      },
                    ],
                  },
                },
              ]}
              labels={({ index, datum, ...props }) =>
                narrative && datum.hasInsights
                  ? `Week of ${format(
                      parseISOLocal(datum.date),
                      "MMMM dd, yyyy"
                    )}`
                  : ""
              }
              size={4}
              labelComponent={
                <VictoryTooltip
                  active={({ datum }) => {
                    if (!milestonesForCurrentNarrative[activeMilestone]) return;
                    return (
                      milestonesForCurrentNarrative[activeMilestone]
                        .timelineDate === parseISOLocal(datum.date)
                    );
                  }}
                  constrainToVisibleArea
                  pointerLength={0}
                  style={{ fill: "white" }}
                  flyoutStyle={{ fill: "black" }}
                  dy={-10}
                />
              }
            />
            <VictoryAxis
              offsetY={50}
              tickCount={12}
              tickFormat={(date) =>
                date.toLocaleString("default", { month: "short" }).toUpperCase()
              }
              style={{
                axis: { stroke: "transparent" },
                tickLabels: { fill: "black" },
              }}
            />
          </VictoryChart>
        ) : null,
      ].filter((c) => c !== null)
    );
  });

  const [activeMilestone, setActiveMilestone] = useState(-1);

  const milestonesForCurrentNarrative = useMemo(
    () =>
      narrativeMilestones
        .filter(
          ({ narrative: milestoneNarrative }) =>
            milestoneNarrative === narrative
        )
        .sort((a, b) => a.timelineDate - b.timelineDate),
    [narrative]
  );

  const scrollToActiveMilestone = () => {
    if (narrative === null) return;
    if (!milestonesForCurrentNarrative[activeMilestone]) return;

    const { timelineDate } = milestonesForCurrentNarrative[activeMilestone];

    const dataIndexAtMilestone = dataByNarrative[narrative].findIndex(
      ({ date }) => parseISOLocal(date) === timelineDate
    );
    const dataLength = dataByNarrative[narrative].length;
    const progress = dataIndexAtMilestone / dataLength;

    const graphWindowScrollWidth = graphWindowRef.current.scrollWidth;
    const graphWindowWidth = graphWindowRef.current.offsetWidth;

    const objectPosition = progress * graphWindowScrollWidth;

    graphWindowRef.current.scrollTo({
      left: objectPosition - graphWindowWidth / 2,
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToActiveMilestone();
  }, [narrative, activeMilestone, dataWindowSize]);

  const scroll = (progress) => {
    const graphWindowScrollWidth = graphWindowRef.current.scrollWidth;
    const graphWindowWidth = graphWindowRef.current.offsetWidth;

    const scrollableWidth = graphWindowScrollWidth - graphWindowWidth;

    const scrollTo = progress * scrollableWidth;

    graphWindowRef.current.scrollTo({
      left: scrollTo,
      top: 0,
    });
  };

  const narrativeSummaryContent = () => (
    <InfoPanelInnerWrapper>
      <NarrativeInfoPanel backgroundColor={COLORS.WHITE} className="pb-5">
        <Title>
          <TitleIcon>category</TitleIcon>
          <TitleText>Narrative</TitleText>
        </Title>
        <NarrativeTitle>{narrative}</NarrativeTitle>
        <p>{getLongNarrativeDescription(narrative)}</p>
      </NarrativeInfoPanel>
    </InfoPanelInnerWrapper>
  );

  const narrativeMilestoneContent = () => {
    if (!milestonesForCurrentNarrative[activeMilestone]) return;
    const milestone = milestonesForCurrentNarrative[activeMilestone];

    const mediaArtifacts = milestone.mediaArtifacts.map((post) => {
      const id = post.content;
      const activeAudience = null;
      const shouldTrackInsights = false;
      const filters = [];
      const isInsightFocused = false;
      const postIsFocused = false;
      switch (post.type) {
        case POST_TYPES.ARTICLE:
          return (
            <ArticlePost
              key={id}
              id={id}
              name={id}
              activeAudience={activeAudience}
              showPostFooter={true}
              {...post}
              filters={filters}
              shouldTrackInsights={shouldTrackInsights}
              onInsightPostEnterView={(inView) => {
                return;
              }}
              anyPostIsFocused={isInsightFocused}
              postIsFocused={postIsFocused}
            />
          );
        case POST_TYPES.VIDEO:
          return (
            <VideoPost
              key={id}
              id={id}
              name={id}
              activeAudience={activeAudience}
              showPostFooter={true}
              {...post}
              filters={filters}
              shouldTrackInsights={shouldTrackInsights}
              onInsightPostEnterView={(inView) =>
                handleInsightPostEnterView(post, id, inView)
              }
              anyPostIsFocused={isInsightFocused}
              postIsFocused={postIsFocused}
            />
          );
        case POST_TYPES.PHOTO:
          return (
            <PhotoPost
              key={id}
              id={id}
              name={id}
              activeAudience={activeAudience}
              showPostFooter={true}
              {...post}
              filters={filters}
              shouldTrackInsights={shouldTrackInsights}
              onInsightPostEnterView={(inView) =>
                handleInsightPostEnterView(post, id, inView)
              }
              anyPostIsFocused={isInsightFocused}
              postIsFocused={postIsFocused}
            />
          );
        case POST_TYPES.MUSIC:
          return (
            <MusicPost
              key={id}
              id={id}
              name={id}
              activeAudience={activeAudience}
              showPostFooter={true}
              {...post}
              filters={filters}
              shouldTrackInsights={shouldTrackInsights}
              onInsightPostEnterView={(inView) =>
                handleInsightPostEnterView(post, id, inView)
              }
              anyPostIsFocused={isInsightFocused}
              postIsFocused={postIsFocused}
            />
          );
        case POST_TYPES.TWITTER:
          return (
            <TwitterPost
              key={id}
              id={id}
              name={id}
              activeAudience={activeAudience}
              showPostFooter={true}
              {...post}
              filters={filters}
              shouldTrackInsights={shouldTrackInsights}
              onInsightPostEnterView={(inView) =>
                handleInsightPostEnterView(post, id, inView)
              }
              anyPostIsFocused={isInsightFocused}
              postIsFocused={postIsFocused}
            />
          );
        case POST_TYPES.TV:
          return (
            <TvPost
              key={id}
              id={id}
              name={id}
              activeAudience={activeAudience}
              showPostFooter={true}
              {...post}
              filters={filters}
              shouldTrackInsights={shouldTrackInsights}
              onInsightPostEnterView={(inView) =>
                handleInsightPostEnterView(post, id, inView)
              }
              anyPostIsFocused={isInsightFocused}
              postIsFocused={postIsFocused}
            />
          );
        default:
          return null;
      }
    });

    return (
      <>
        <NarrativeInfoPanel backgroundColor={COLORS.WHITE} className="pb-5">
          <Title>
            <TitleIcon>date_range</TitleIcon>
            <TitleText>
              {format(milestone.timelineDate, "MMMM, yyyy")}
            </TitleText>
          </Title>
          <NarrativeTitle>{milestone.title}</NarrativeTitle>
          <InfoPanelContent>
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              components={{
                strong: MarkdownStrong,
              }}
            >
              {milestone.insights}
            </ReactMarkdown>
            {getNarrativeDescription(highlightedNarrative)}
          </InfoPanelContent>
          <EditLink filePath={milestone.filePath} />
        </NarrativeInfoPanel>
        {mediaArtifacts}
      </>
    );
  };

  const narrativeInfoContent = () => (
    // TODO: Add the collapsed logic
    <InfoPanelInnerWrapper className="mt-5">
      {activeMilestone >= 0
        ? narrativeMilestoneContent()
        : narrativeSummaryContent()}

      <Pager
        nextDisabled={
          milestonesForCurrentNarrative.length <= activeMilestone + 1
        }
        previousDisabled={activeMilestone < 0}
        next={nextMilestone}
        previous={previousMilestone}
        total={milestonesForCurrentNarrative.length + 1}
        current={activeMilestone === null ? 1 : activeMilestone + 2}
        isCollapsed={false}
      />
    </InfoPanelInnerWrapper>
  );
  const previousMilestone = () => {
    incrementMilestone(-1);
  };

  const nextMilestone = () => {
    incrementMilestone(1);
  };

  const incrementMilestone = (by) =>
    setActiveMilestone((prevActiveMilestone) =>
      Math.min(
        Math.max(prevActiveMilestone + by, -1),
        milestonesForCurrentNarrative.length - 1
      )
    );

  const setActiveMilestoneByDate = (date) => {
    const milestoneIndex = milestonesForCurrentNarrative.findIndex(
      ({ timelineDate }) => parseISOLocal(date) === timelineDate
    );

    setActiveMilestone(milestoneIndex);
  };

  const narrativesSummaryContent = () => (
    <InfoPanelInnerWrapper>
      <NarrativeInfoPanel backgroundColor={COLORS.WHITE} className="mb-5">
        <Title>
          <TitleIcon>info</TitleIcon>
          <TitleText>Key</TitleText>
        </Title>
        <div>
          {NARRATIVE_TYPES_ARRAY.map((narrativeType) => (
            <Link href={"/narratives/" + getNarrativeSlug(narrativeType)}>
              <NarrativeButton
                key={narrativeType}
                className="px-1 py-0 m-1"
                backgroundColor={getNarrativeTypeColor(narrativeType)}
                onMouseEnter={() => highlightNarrative(narrativeType)}
                onMouseLeave={() =>
                  highlightNarrative((prevHighlightedNarrative) =>
                    prevHighlightedNarrative === narrativeType
                      ? null
                      : prevHighlightedNarrative
                  )
                }
              >
                {narrativeType}
              </NarrativeButton>
            </Link>
          ))}
        </div>
      </NarrativeInfoPanel>
      <NarrativeInfoPanel expandable narrative={highlightedNarrative}>
        {highlightedNarrative && (
          <>
            <Title>
              <TitleIcon>info</TitleIcon>
              <TitleText>{highlightedNarrative}</TitleText>
            </Title>
            <p>{getNarrativeDescription(highlightedNarrative)}</p>
          </>
        )}
        {!highlightedNarrative && (
          <p>
            Hover over a point in the graph to learn more about a specific
            narrative.
          </p>
        )}
      </NarrativeInfoPanel>
    </InfoPanelInnerWrapper>
  );

  const handleScroll = (event) => {
    const { scrollWidth, offsetWidth, scrollLeft } = graphWindowRef.current;
    const progress = scrollLeft / (scrollWidth - offsetWidth);
    setScrollProgress(progress);
  };

  const [scrollProgress, setScrollProgress] = useState(0);

  const handleKeyUp = (event) => {
    switch (event.key) {
      case "Escape":
        router.push("/narratives/compare");
        break;
      case "j":
      case "ArrowRight":
        nextMilestone();
        break;
      case "k":
      case "ArrowLeft":
        previousMilestone();
        break;
      case "n":
      case "ArrowDown":
        nextNarrative();
        break;
      case "p":
      case "ArrowUp":
        previousNarrative();
        break;
    }
  };

  const nextNarrative = () => {
    const i = NARRATIVE_TYPES_ARRAY.indexOf(narrative);
    const next = (i + 1) % NARRATIVE_TYPES_ARRAY.length;
    router.push(`/narratives/${getNarrativeSlug(NARRATIVE_TYPES_ARRAY[next])}`);
  };

  const previousNarrative = () => {
    const i = NARRATIVE_TYPES_ARRAY.indexOf(narrative);
    const previous =
      (i - 1 + NARRATIVE_TYPES_ARRAY.length) % NARRATIVE_TYPES_ARRAY.length;
    router.push(
      `/narratives/${getNarrativeSlug(NARRATIVE_TYPES_ARRAY[previous])}`
    );
  };

  const setGraphSize = () => {
    if (!graphWindowRef.current) return;

    const { offsetWidth, offsetHeight } = graphWindowRef.current;

    setGraphWidth(offsetWidth);
    setGraphHeight(offsetHeight);
  };

  useEffect(() => {
    graphWindowRef.current.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", setGraphSize);
    window.addEventListener("resize", scrollToActiveMilestone);

    setGraphSize();

    return () => {
      graphWindowRef.current.addEventListener("scroll", handleScroll);
      window.removeEventListener("resize", setGraphSize);
      window.removeEventListener("resize", scrollToActiveMilestone);
    };
  }, []);

  useEffect(() => {
    document.addEventListener("keyup", handleKeyUp);

    return () => {
      document.removeEventListener("keyup", handleKeyUp);
    };
  }, [narrative]);

  return (
    <Wrapper>
      <Head>
        <title>
          {narrative ? `Narratives - ${narrative}` : "Compare Narratives"}
        </title>
      </Head>
      <InfoPanelWrapper>
        {narrative ? narrativeInfoContent() : narrativesSummaryContent()}
      </InfoPanelWrapper>
      <MainDisplay>
        <GraphWrapper ref={graphWindowRef}>
          <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
            <NarrativeSVGGradients />
            {areas}
          </svg>
        </GraphWrapper>
        <ScrollControl
          labels={dateLabelsWithSize}
          onScroll={scroll}
          targetProgress={scrollProgress}
          scrollerWidth={(dataWindowSize / (totalDataSize * 7)) * 100}
        />
        <GraphAxis src="/images/graph-axis.svg" />
        {activeMedia === null || activeMedia === "twitter" ? (
          <TwitterNote>
            <strong>Note:</strong> Twitter data unavailable as of January 2022.
          </TwitterNote>
        ) : null}
      </MainDisplay>
      <Nav
        showFeedProfileToggle={false}
        breadCrumbs={[
          <Link href="/narratives">
            <a href="/narratives">Narratives</a>
          </Link>,
          <span>{narrative || "Compare"}</span>,
        ]}
        navBackgroundColor={COLORS.LIGHTEST_GRAY}
        secondaryNav={
          <>
            <FlexSpacer />
            <NarrativeNav
              activeMedia={activeMedia}
              activeNarrative={narrative}
              handleMediaChange={(slug) => setActiveMedia(slug)}
              dataWindowSize={dataWindowSize}
              handleDataWindowSizeChange={setDataWindowSize}
            />
          </>
        }
      />
    </Wrapper>
  );
}
